import type { Role } from "@/lib/auth/rbac";

// ---------- auth ----------

export interface LoginResponse {
  userId: string;
  username: string;
  fullName: string;
  role: Role;
  /** null for SUPER_ADMIN (platform) accounts. */
  tenantId: number | null;
  accessToken: string;
  expiresInSeconds: number;
}

export interface MeResponse {
  userId: string;
  username: string;
  fullName: string;
  role: Role;
  tenantId: number | null;
}

// ---------- tenants (businesses) ----------

/** What kind of business a tenant runs — shapes the whole app for its staff. */
export type BusinessType = "RESTAURANT" | "DUKA" | "HARDWARE" | "AUTO_SPARES" | "LIQUOR_STORE";

export const BUSINESS_TYPES: { value: BusinessType; label: string }[] = [
  { value: "RESTAURANT", label: "Restaurant" },
  { value: "DUKA", label: "Shop (duka)" },
  { value: "HARDWARE", label: "Hardware / construction" },
  { value: "AUTO_SPARES", label: "Auto spares" },
  { value: "LIQUOR_STORE", label: "Liquor store" },
];

export const businessTypeLabel = (t: string | null | undefined) =>
  BUSINESS_TYPES.find((b) => b.value === t)?.label ?? "Restaurant";

export const isShop = (t: string | null | undefined) => !!t && t !== "RESTAURANT";

/** How a shop sells — the app's add-stock form and till follow it. Shops only. */
export type SaleMode = "RETAIL" | "WHOLESALE";

export const SALE_MODES: { value: SaleMode; label: string }[] = [
  { value: "RETAIL", label: "Retail (rejareja) — by the piece" },
  { value: "WHOLESALE", label: "Wholesale (jumla) — by the carton" },
];

export const saleModeLabel = (m: string | null | undefined) => (m === "WHOLESALE" ? "Wholesale" : "Retail");

export interface TenantDto {
  id: number;
  name: string;
  address: string | null;
  phoneNumber: string | null;
  businessType: BusinessType;
  saleMode: SaleMode;
  active: boolean;
  createdAt: string;
}

export interface CreateTenantRequest {
  name: string;
  address?: string;
  phoneNumber?: string;
  businessType?: BusinessType;
  saleMode?: SaleMode;
}

export interface UpdateTenantRequest {
  name?: string;
  address?: string;
  phoneNumber?: string;
  active?: boolean;
  businessType?: BusinessType;
  saleMode?: SaleMode;
}

// ---------- users ----------

export interface UserDto {
  id: string;
  /** null for SUPER_ADMIN (platform) accounts. */
  tenantId: number | null;
  username: string;
  fullName: string;
  role: Role;
  active: boolean;
  createdAt?: string;
}

export interface CreateUserRequest {
  /** Required for MANAGER/WAITER/KITCHEN, null/omitted for SUPER_ADMIN. */
  tenantId: number | null;
  username: string;
  password: string;
  fullName: string;
  role: Role;
}

export interface UpdateUserRequest {
  fullName?: string;
  role?: Role;
  active?: boolean;
  password?: string;
}

// ---------- menu ----------

export type Station = "KITCHEN" | "BAR";

export interface MenuItemDto {
  id: string;
  categoryId: string;
  name: string;
  price: number;
  station: Station;
  available: boolean;
  sortOrder: number;
  imageUrl: string | null;
}

export interface MenuCategoryDto {
  id: string;
  name: string;
  sortOrder: number;
  active?: boolean;
  items?: MenuItemDto[];
}

export interface CategoryRequest {
  name: string;
  sortOrder: number;
  active: boolean;
}

export interface MenuItemRequest {
  categoryId: string;
  name: string;
  price: number;
  station: Station;
  available: boolean;
  sortOrder: number;
}

// ---------- orders (read-only in the admin portal) ----------

export type OrderStatus = "OPEN" | "SERVED" | "PAID" | "CANCELLED";
export type OrderItemStatus = "PENDING" | "PREPARING" | "READY" | "SERVED";
export type PaymentMethod = "CASH" | "MOBILE" | "CARD";

export interface OrderItemDto {
  id: string;
  menuItemId: string;
  itemName: string;
  unitPrice: number;
  qty: number;
  lineTotal: number;
  station: Station;
  status: OrderItemStatus;
  note: string | null;
  createdAt: string;
  readyAt: string | null;
}

export interface OrderDto {
  id: string;
  tenantId: number;
  orderNumber: string;
  tableLabel: string;
  waiterId: string;
  waiterName: string;
  status: OrderStatus;
  total: number;
  note: string | null;
  deviceId: string;
  createdAt: string;
  receivedAt: string | null;
  paidAt: string | null;
  cancelledAt: string | null;
  cancelReason: string | null;
  items: OrderItemDto[];
}

// ---------- sales ----------

export interface SalesByMethod {
  method: PaymentMethod;
  total: number;
}

export interface SalesByHour {
  hour: number;
  total: number;
}

/** `GET /inventory/shop-summary` — a shop's day, plus stock and debts. */
export interface ShopSummary {
  todayTotal: number;
  todayCount: number;
  todayCost: number;
  todayProfit: number;
  todayByMethod: { method: string; total: number }[];
  topSellers: { itemId: number; itemName: string; quantity: number; revenue: number }[];
  weekTotal: number;
  monthTotal: number;
  stockValue: number;
  lowStockCount: number;
  outOfStockCount: number;
  outstandingDebt: number;
  debtCount: number;
  overdueDebtCount: number;
  todayDebtCollected: number;
}

export interface SalesToday {
  todayGross: number;
  orderCount: number;
  openOrders: number;
  avgOrder: number;
  byMethod: SalesByMethod[];
  byHour: SalesByHour[];
}

export interface SalesSummaryRow {
  date: string;
  gross: number;
  orders: number;
}

export interface TopItemRow {
  itemName: string;
  qty: number;
  total: number;
}
